
import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useResellerPlans } from '@/hooks/license/useResellerPlans';
import BusinessInfoFields from './phoneOrder/BusinessInfoFields';
import OrderInfoFields from './phoneOrder/OrderInfoFields';

const phoneOrderSchema = z.object({
  businessName: z.string().min(1, 'Business name is required'),
  contactName: z.string().min(1, 'Contact name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().min(1, 'Phone number is required'),
  planId: z.string().min(1, 'Please select a plan'),
  paymentMethod: z.string().min(1, 'Payment method is required'),
  amount: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Amount must be a valid number'),
  domain: z.string().optional(),
  agreeTerms: z.literal(true, {
    errorMap: () => ({ message: 'You must agree to the terms' }),
  }),
});

type PhoneOrderValues = z.infer<typeof phoneOrderSchema>;

interface PhoneOrderFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const PhoneOrderForm: React.FC<PhoneOrderFormProps> = ({ onSuccess, onCancel }) => {
  const { toast } = useToast();
  const { resellerPlans } = useResellerPlans(false);

  const form = useForm<PhoneOrderValues>({
    resolver: zodResolver(phoneOrderSchema),
    defaultValues: {
      businessName: '',
      contactName: '',
      email: '',
      phone: '',
      planId: '',
      paymentMethod: 'credit_card',
      amount: '',
      domain: '',
      agreeTerms: false as unknown as true,
    },
  });

  const onSubmit = async (data: PhoneOrderValues) => {
    try {
      console.log('Submitting phone order:', data);
      
      // Here you would submit to your API
      // Simulating API call with timeout
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Order Created",
        description: "The reseller order has been created successfully.",
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error creating order:', error);
      toast({
        title: "Error",
        description: "Failed to create the order. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <BusinessInfoFields form={form} />
          </div>
          
          <div className="space-y-4">
            <OrderInfoFields form={form} resellerPlans={resellerPlans} />
          </div>
        </div>
        
        <FormField
          control={form.control}
          name="agreeTerms"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  I confirm that payment has been received and the customer has agreed to the terms of service
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        
        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            Create Order
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default PhoneOrderForm;
